import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';

// ── Tree View ────────────────────────────────────────────────────────────────
const TreeNode = ({ node, level = 0 }) => {
    const [expanded, setExpanded] = useState(true);
    if (Array.isArray(node)) {
        return (
            <ul style={{ listStyle: 'none', paddingLeft: 16, margin: 0 }}>
                {node.map((child, idx) => <TreeNode key={idx} node={child} level={level + 1} />)}
            </ul>
        );
    }
    if (node && typeof node === 'object' && node.children && node.children.length > 0) {
        return (
            <li style={{ marginBottom: 8 }}>
                <span style={{ cursor: 'pointer', fontWeight: '600', color: '#1565c0', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setExpanded(!expanded)}>
                    <i className={`bi bi-caret-${expanded ? 'down' : 'right'}-fill`} style={{ fontSize: 11 }}></i>
                    {node.field || node.type || 'Group'}
                    {node.operator && <span style={{ color: '#6b7280', fontWeight: 400 }}>{node.operator}</span>}
                    {node.value && <span style={{ color: '#16a34a', fontWeight: 500 }}>{node.value}</span>}
                </span>
                {expanded && (
                    <ul style={{ listStyle: 'none', paddingLeft: 20, margin: 0 }}>
                        <TreeNode node={node.children} level={level + 1} />
                    </ul>
                )}
            </li>
        );
    }
    return (
        <li style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1565c0', flexShrink: 0, display: 'inline-block' }}></span>
            {node.field && <strong style={{ color: '#111827' }}>{node.field}</strong>}
            {node.operator && <span style={{ color: '#6b7280', background: '#f3f4f6', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>{node.operator}</span>}
            {node.value && <span style={{ color: '#16a34a', fontWeight: 500 }}>{node.value}</span>}
        </li>
    );
};

// ── Action Menu ──────────────────────────────────────────────────────────────
const ActionMenu = ({ segment, onToggle, onEdit }) => {
    const [open, setOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleToggle = () => {
        if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            // If less than 150px space below, open upward
            setOpenUpward(window.innerHeight - rect.bottom < 150);
        }
        setOpen(!open);
    };

    const items = [
        {
            icon: segment.status ? 'bi-pause-circle' : 'bi-play-circle',
            label: segment.status ? 'Deactivate' : 'Activate',
            color: segment.status ? '#dc2626' : '#16a34a',
            onClick: () => { onToggle(segment._id, segment.status); setOpen(false); }
        },
        {
            icon: 'bi-pencil-square',
            label: 'Edit Segment',
            color: '#1565c0',
            onClick: () => { onEdit(segment); setOpen(false); }
        },
    ];

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button style={menuStyles.trigger} onClick={handleToggle}>
                <i className="bi bi-three-dots-vertical"></i>
            </button>
            {open && (
                <div style={{ ...menuStyles.menu, ...(openUpward ? { bottom: '36px', top: 'auto' } : { top: '36px' }) }}>
                    {items.map((item, i) => (
                        <button key={i} style={menuStyles.item} onClick={item.onClick}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                            <span style={{ ...menuStyles.icon, background: item.color + '15', color: item.color }}>
                                <i className={`bi ${item.icon}`}></i>
                            </span>
                            <span style={{ ...menuStyles.label, color: item.color }}>{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const menuStyles = {
    trigger: { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', fontSize: '15px' },
    menu: { position: 'absolute', right: 0, background: 'white', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', minWidth: '170px', zIndex: 1000, padding: '6px' },
    item: { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '8px 10px', border: 'none', background: 'white', borderRadius: '6px', cursor: 'pointer', textAlign: 'left' },
    icon: { width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 },
    label: { fontSize: '13px', fontWeight: '500' },
};

// ── Main Component ───────────────────────────────────────────────────────────
const SegmentationComponent = () => {
    const navigate = useNavigate();
    const [segments, setSegments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const itemsPerPage = 10;

    const fetchSegments = useCallback(async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/segmentation`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token },
            });
            if (!response.ok) throw new Error('Failed to fetch segments');
            const data = await response.json();
            setSegments(data);
        } catch (error) {
            console.error('Error fetching segments:', error);
            setError('Failed to load segments. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchSegments(); }, [fetchSegments]);
    useEffect(() => { setCurrentPage(1); }, [statusFilter]);

    const handleToggle = async (segmentId, currentStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/segmentation/${segmentId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ status: !currentStatus }),
            });
            if (response.ok) {
                setSegments(prev => prev.map(s => s._id === segmentId ? { ...s, status: !currentStatus } : s));
                toast.success(`Segment ${currentStatus ? 'deactivated' : 'activated'} successfully!`, { autoClose: 2000 });
            } else {
                toast.error('Failed to update status.', { autoClose: 2000 });
            }
        } catch (error) {
            toast.error('An error occurred. Please try again.', { autoClose: 2000 });
        }
    };

    const handleEdit = (segment) => {
        navigate('/editsegment', {
            state: {
                segmentId: segment._id,
                segmentJson: segment.segment_json,
                segmentNameProp: segment.name,
                segmentDescProp: segment.description,
            }
        });
    };

    const filteredSegments = segments.filter(s =>
        statusFilter === '' || s.status === (statusFilter === 'true')
    );

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedSegments = filteredSegments.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredSegments.length / itemsPerPage);

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Segmentation</h5>
                        <p style={styles.pageSubtitle}>Create and manage your customer segments</p>
                    </div>
                    <button style={styles.btnPrimary} onClick={() => navigate('/createsegmentation')}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>
                        Create Segment
                    </button>
                </div>

                {/* Filters */}
                <div style={styles.filterRow}>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Status</label>
                        <select style={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    {statusFilter && (
                        <button style={styles.btnSecondary} onClick={() => setStatusFilter('')}>
                            <i className="bi bi-x" style={{ marginRight: 4 }}></i>Reset
                        </button>
                    )}
                    <span style={styles.resultCount}>{filteredSegments.length} segment{filteredSegments.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Table card */}
                <div style={styles.card}>
                    {isLoading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}></div>
                            <p style={styles.stateText}>Loading segments…</p>
                        </div>
                    ) : error ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-exclamation-circle" style={{ fontSize: 32, color: '#ef4444' }}></i>
                            <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                            <button style={styles.btnPrimary} onClick={fetchSegments}>Retry</button>
                        </div>
                    ) : filteredSegments.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-funnel" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>{statusFilter ? 'No segments match this filter.' : 'No segments found. Create your first segment.'}</p>
                            {!statusFilter && <button style={styles.btnPrimary} onClick={() => navigate('/createsegmentation')}>Create Segment</button>}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHead}>
                                        <th style={styles.th}>Segment Name</th>
                                        <th style={styles.th}>Created On</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Rules</th>
                                        <th style={styles.th}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedSegments.map((segment) => (
                                        <tr key={segment._id} style={styles.tr}>
                                            <td style={styles.td}><strong>{segment.name}</strong></td>
                                            <td style={styles.td}>{segment.createdAt}</td>
                                            <td style={styles.td}>
                                                <span style={{ ...styles.badge, ...(segment.status ? styles.badgeActive : styles.badgeInactive) }}>
                                                    {segment.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <button style={styles.viewBtn} onClick={() => setSelectedSegment(segment)}>
                                                    <i className="bi bi-eye" style={{ marginRight: 5 }}></i>
                                                    View Rules
                                                </button>
                                            </td>
                                            <td style={styles.td}>
                                                <ActionMenu segment={segment} onToggle={handleToggle} onEdit={handleEdit} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!isLoading && !error && filteredSegments.length > 0 && (
                    <PaginationComponent currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                )}

                <ToastContainer />

                {/* View Rules Modal */}
                {selectedSegment && (
                    <div style={styles.modalOverlay} onClick={() => setSelectedSegment(null)}>
                        <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
                            {/* Modal Header */}
                            <div style={styles.modalHeader}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={styles.modalIcon}>
                                        <i className="bi bi-funnel-fill" style={{ fontSize: 16, color: '#1565c0' }}></i>
                                    </div>
                                    <div>
                                        <h6 style={styles.modalTitle}>{selectedSegment.name}</h6>
                                        <p style={styles.modalSubtitle}>
                                            {selectedSegment.segment_json?.length || 0} rule{(selectedSegment.segment_json?.length || 0) !== 1 ? 's' : ''} defined
                                            {selectedSegment.description && ` · ${selectedSegment.description}`}
                                        </p>
                                    </div>
                                </div>
                                <button style={styles.closeBtn} onClick={() => setSelectedSegment(null)}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div style={styles.modalBody}>
                                {selectedSegment.segment_json && selectedSegment.segment_json.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {selectedSegment.segment_json.map((rule, idx) => (
                                            <div key={idx} style={styles.ruleCard}>
                                                {/* Rule header */}
                                                <div style={styles.ruleCardHeader}>
                                                    <span style={styles.ruleIndex}>Rule {idx + 1}</span>
                                                    {rule.children?.length > 0 && (
                                                        <span style={styles.conditionBadge}>
                                                            {rule.children.length} condition{rule.children.length !== 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Rule body */}
                                                <div style={styles.ruleCondition}>
                                                    <span style={styles.ruleField}>{rule.field}</span>
                                                    <span style={styles.ruleOperator}>{rule.operator}</span>
                                                    <span style={styles.ruleValue}>{rule.value}</span>
                                                </div>
                                                {/* Children */}
                                                {rule.children?.length > 0 && (
                                                    <div style={styles.childrenWrapper}>
                                                        {rule.children.map((child, cidx) => (
                                                            <div key={cidx} style={styles.childRule}>
                                                                <i className="bi bi-arrow-return-right" style={{ color: '#9ca3af', fontSize: 12, marginRight: 8 }}></i>
                                                                <span style={styles.ruleField}>{child.field}</span>
                                                                <span style={styles.ruleOperator}>{child.operator}</span>
                                                                <span style={styles.ruleValue}>{child.value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={styles.emptyRules}>
                                        <i className="bi bi-funnel" style={{ fontSize: 32, color: '#d1d5db' }}></i>
                                        <p style={{ margin: 0, fontSize: 14, color: '#9ca3af' }}>No rules defined for this segment.</p>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div style={styles.modalFooter}>
                                <span style={styles.modalStatus}>
                                    <span style={{ ...styles.badge, ...(selectedSegment.status ? styles.badgeActive : styles.badgeInactive) }}>
                                        {selectedSegment.status ? 'Active' : 'Inactive'}
                                    </span>
                                </span>
                                <button style={styles.btnSecondary} onClick={() => setSelectedSegment(null)}>Close</button>
                                <button style={styles.btnPrimary} onClick={() => { handleEdit(selectedSegment); setSelectedSegment(null); }}>
                                    <i className="bi bi-pencil-square" style={{ marginRight: 6 }}></i>Edit Segment
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    filterRow: { display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    filterLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' },
    filterSelect: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151', background: 'white', minWidth: 140 },
    resultCount: { fontSize: '13px', color: '#6b7280', marginLeft: 'auto', alignSelf: 'center' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', borderBottom: '2px solid #bfdbfe' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '12px 14px', color: '#374151', verticalAlign: 'middle' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    badgeActive: { background: '#dcfce7', color: '#16a34a' },
    badgeInactive: { background: '#fee2e2', color: '#dc2626' },
    viewBtn: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1565c0', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    modalBox: { background: 'white', borderRadius: '12px', width: '100%', maxWidth: '500px', maxHeight: '80vh', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc', flexShrink: 0 },
    modalIcon: { width: '40px', height: '40px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    modalTitle: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' },
    modalSubtitle: { margin: '2px 0 0', fontSize: '12px', color: '#6b7280' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', padding: 0, flexShrink: 0 },
    modalBody: { padding: '16px 20px', overflowY: 'auto', flex: 1 },
    modalFooter: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f3f4f6', background: '#f8fafc', flexShrink: 0 },
    modalStatus: { marginRight: 'auto' },
    ruleCard: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px', borderLeft: '3px solid #1565c0' },
    ruleCardHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
    ruleIndex: { fontSize: '11px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.05em' },
    conditionBadge: { fontSize: '11px', background: '#eff6ff', color: '#1565c0', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' },
    ruleCondition: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    ruleField: { fontSize: '13px', fontWeight: '700', color: '#111827' },
    ruleOperator: { fontSize: '12px', background: '#e5e7eb', color: '#374151', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' },
    ruleValue: { fontSize: '13px', fontWeight: '600', color: '#16a34a' },
    childrenWrapper: { marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb', display: 'flex', flexDirection: 'column', gap: 6 },
    childRule: { display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, flexWrap: 'wrap' },
    emptyRules: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 20px' },
};

export default SegmentationComponent;
